import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useGlobalStatus } from '@/hooks/useGlobalStatus';
import { useAIGeneration, RightViewType } from '@/hooks/useAIGeneration';
import { useResumeLibrary } from '@/hooks/useResumeLibrary';
import { useSavedOutputs } from '@/hooks/useSavedOutputs';
import { SavedOutput } from '@/api/types';
import { TargetDetails } from '@/types/workspace';
import toast from 'react-hot-toast';

interface WorkspaceContextValue {
  id: string;
  isActive: boolean;
  
  // Resumes
  resumes: any[];
  selectedId: string | null;
  selectResume: (id: string | null) => void;
  deleteResume: (id: string) => void;
  uploadResume: (base64: string, name: string, label: string) => Promise<any>;

  // Job Details
  jobText: string;
  setJobText: (txt: string) => void;
  jobLink: string;
  setJobLink: (link: string) => void;
  isFetchingLink: boolean;
  setIsFetchingLink: (val: boolean) => void;
  isExtracting: boolean;
  extractMetadataForText: (txt: string) => Promise<any>;
  targetDetails: TargetDetails;
  setTargetDetails: (details: any) => void;
  hasInputs: boolean;
  isWorkspaceLoaded: boolean;

  // Status
  status: string;
  setStatus: (status: any) => void;
  statusMessage: string;
  setStatusMessage: (msg: string) => void;
  isSaving: boolean;
  isWorking: boolean;

  // Generation
  reviewResult: any;
  setReviewResult: (res: any) => void;
  coverLetterResult: any;
  setCoverLetterResult: (res: any) => void;
  tailoredResumeResult: any;
  setTailoredResumeResult: (res: any) => void;
  interviewPrepResult: any;
  setInterviewPrepResult: (res: any) => void;
  communicationsResult: any;
  setCommunicationsResult: (res: any) => void;
  rightView: RightViewType;
  setRightView: (view: RightViewType) => void;
  activeAnalysisTab: string;
  setActiveAnalysisTab: (tab: string) => void;
  isGenerating: Record<string, boolean>;
  cancelGeneration: () => void;
  
  // Actions
  handleAnalyze: () => void;
  handleGenerateCoverLetter: () => void;
  handleGenerateTailoredResume: () => void;
  handleGenerateInterviewPrep: () => void;
  handleGenerateCommunications: () => void;
  handleGeneratePackage: () => void;

  // Saving Outputs
  outputs: SavedOutput[];
  openedOutputId: string | null;
  handleSaveOutput: () => Promise<void>;
  handleUpdateStatus: (outputId: string, updates: Partial<SavedOutput>) => Promise<void>;
  handleOpenOutput: (output: any) => void;
  deleteOutput: (id: string) => Promise<any>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export const useWorkspaceContext = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspaceContext must be used inside WorkspaceProvider");
  return ctx;
};

interface WorkspaceProviderProps {
  id: string;
  isActive: boolean;
  recordUsage: (cost: number, promptToken: number, completionToken: number) => void;
  fireWebhook: (event: string, payload: any) => void;
  children: React.ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ id, isActive, recordUsage, fireWebhook, children }) => {
  const { resumes, selectedId, selectResume, deleteResume, uploadResume } = useResumeLibrary(id);
  const { outputs, saveOutput, updateOutput, deleteOutput } = useSavedOutputs(id);

  const { status, setStatus, statusMessage, setStatusMessage, isSaving, setIsSaving, resetStatus } = useGlobalStatus();
  
  const { 
    jobText, setJobText, jobLink, setJobLink, targetDetails, setTargetDetails,
    isFetchingLink, setIsFetchingLink, isExtracting, extractMetadataForText, resetTargetParams,
    isLoaded: isWorkspaceLoaded
  } = useWorkspace(id, recordUsage);

  const {
    reviewResult, setReviewResult, coverLetterResult, setCoverLetterResult,
    interviewPrepResult, setInterviewPrepResult, communicationsResult, setCommunicationsResult,
    tailoredResumeResult, setTailoredResumeResult,
    rightView, setRightView, activeAnalysisTab, setActiveAnalysisTab,
    handleAnalyze, handleGenerateCoverLetter, handleGenerateTailoredResume, handleGenerateInterviewPrep, handleGenerateCommunications, handleGeneratePackage,
    resetResults, isGenerating, cancelGeneration
  } = useAIGeneration({
    workspaceId: id, selectedId, jobText, targetDetails, setStatus, setStatusMessage, recordUsage
  });

  const [openedOutputId, setOpenedOutputId] = useState<string | null>(null);

  useEffect(() => {
    const handleCustomToast = (e: any) => {
      if (e.detail) toast.success(e.detail);
    };
    window.addEventListener('resumepro:show-toast', handleCustomToast);
    return () => window.removeEventListener('resumepro:show-toast', handleCustomToast);
  }, []);

  const handleSaveOutput = async () => {
    setIsSaving(true);
    try {
      let title = 'Application Package';
      if (targetDetails.company && targetDetails.position) {
        title = `${targetDetails.company} - ${targetDetails.position}`;
      } else if (targetDetails.company) {
        title = `${targetDetails.company} Application`;
      } else if (targetDetails.position) {
        title = `${targetDetails.position} Application`;
      } else if (jobText && jobText.trim().length > 0) {
        const snippet = jobText.trim().replace(/\s+/g, ' ').substring(0, 30);
        title = `App: ${snippet}...`;
      } else if (selectedId && resumes.length > 0) {
        const resume = resumes.find(r => r.id === selectedId);
        if (resume) {
          title = `Application: ${resume.label}`;
        } else {
          title = `Application - ${new Date().toLocaleDateString()}`;
        }
      } else {
        title = `Application - ${new Date().toLocaleDateString()}`;
      }

      const meta: any = {};
      if (targetDetails) meta.targetDetails = targetDetails;
      if (jobText) meta.jobText = jobText;
      if (jobLink) meta.jobLink = jobLink;

      const data = {
        reviewResult,
        coverLetterResult,
        tailoredResumeResult,
        interviewPrepResult,
        communicationsResult,
        activeAnalysisTab,
        selectedId,
        rightView
      };

      if (openedOutputId && outputs.some(o => o.id === openedOutputId)) {
        await updateOutput(openedOutputId, { title, data, meta });
        fireWebhook('Application_Updated', { id: openedOutputId, type: 'application', title, meta });
        setStatusMessage('Updated application package');
        toast.success('Successfully updated application package!');
      } else {
        const newOutput = await saveOutput({ type: 'application', title, data, meta });
        setOpenedOutputId(newOutput.id);
        fireWebhook('Application_Saved', { type: 'application', title, meta });
        setStatusMessage('Saved application package');
        toast.success('Successfully saved application package!');
      }

      setStatus('ready');
    } catch (err: any) {
      setStatus('error');
      setStatusMessage(err.message || 'Failed to save application');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStatus = async (outputId: string, updates: Partial<SavedOutput>) => {
    await updateOutput(outputId, updates);
    const item = outputs.find(o => o.id === outputId);
    if (item && updates.status) {
      fireWebhook('Status_Updated', { id: outputId, title: item.title, oldStatus: item.status, newStatus: updates.status, meta: updates.meta || item.meta });
    }
  };

  const handleOpenOutput = (output: any) => {
    if (Object.values(isGenerating).some(Boolean)) {
      toast.error('Cannot open history while a generation is in progress. Please wait for it to finish.');
      return;
    }

    if (output.type === 'application') {
      setOpenedOutputId(output.id);
      if (output.data?.reviewResult) setReviewResult(output.data.reviewResult);
      if (output.data?.coverLetterResult) setCoverLetterResult(output.data.coverLetterResult);
      if (output.data?.interviewPrepResult) setInterviewPrepResult(output.data.interviewPrepResult);
      if (output.data?.communicationsResult) setCommunicationsResult(output.data.communicationsResult);
      if (output.data?.tailoredResumeResult) setTailoredResumeResult(output.data.tailoredResumeResult);
      if (output.data?.activeAnalysisTab) setActiveAnalysisTab(output.data.activeAnalysisTab);
      
      setRightView(output.data?.rightView || 'analysis');
      if (output.data?.selectedId && resumes.some(r => r.id === output.data.selectedId)) {
        selectResume(output.data.selectedId);
      }
    } else if (output.type === 'analysis') {
      setReviewResult(output.data);
      if (output.meta?.activeTab) setActiveAnalysisTab(output.meta.activeTab);
      setRightView('analysis');
    } else if (output.type === 'cover-letter') {
      setCoverLetterResult(output.data);
      setRightView('coverLetter');
    } else if (output.type === 'interview-prep') {
      setInterviewPrepResult(output.data);
      setRightView('interviewPrep');
    } else if (output.type === 'communications') {
      setCommunicationsResult(output.data);
      setRightView('communications');
    } else if (output.type === 'tailored-resume') {
      setTailoredResumeResult(output.data);
      setRightView('tailoredResume');
    }
    if (output.meta?.targetDetails) setTargetDetails(output.meta.targetDetails);
    if (output.meta?.jobText) setJobText(output.meta.jobText);
  };

  const hasInputs = Boolean(selectedId && jobText.trim());
  const isWorking = status === 'working';

  const value: WorkspaceContextValue = {
    id, isActive,
    resumes, selectedId, selectResume, deleteResume, uploadResume,
    jobText, setJobText, jobLink, setJobLink, isFetchingLink, setIsFetchingLink, isExtracting, extractMetadataForText, targetDetails, setTargetDetails, hasInputs, isWorkspaceLoaded,
    status, setStatus, statusMessage, setStatusMessage, isSaving, isWorking,
    reviewResult, setReviewResult, coverLetterResult, setCoverLetterResult, tailoredResumeResult, setTailoredResumeResult, interviewPrepResult, setInterviewPrepResult, communicationsResult, setCommunicationsResult, rightView, setRightView, activeAnalysisTab, setActiveAnalysisTab, isGenerating,
    handleAnalyze, handleGenerateCoverLetter, handleGenerateTailoredResume, handleGenerateInterviewPrep, handleGenerateCommunications, handleGeneratePackage,
    outputs, openedOutputId, handleSaveOutput, handleUpdateStatus, handleOpenOutput, deleteOutput, cancelGeneration
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
